import type * as React from "react";
import { SxProps } from "@mui/system";
import { Typography, Button, Box } from "@mui/material";
import deepmerge from "@mui/utils/deepmerge";
import { COLORS } from "../../Theme/colors";
import Spinner from "./Spinner";

type GLButtonProps = {
  buttonType?:
    | "primary"
    | "secondary"
    | "secondary-transparent"
    | "secondary-no-border";
  loading?: boolean;
  onClick?: () => void;
  text?: string;
  disabled?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  sx?: SxProps;
  id?: string;
  title?: string;
};
type StylesObject = SxProps;

const baseStyles: StylesObject = {
  alignItems: "center",
  // Theme MuiButton text/contained uses 100px; keep GL pill base shape everywhere
  borderRadius: "8px !important",
  cursor: "pointer",
  display: "flex",
  gap: "8px",
  justifyContent: "center",
  maxWidth: "max-content",
  padding: "8px 24px",
  transition: "background-color 0.3s ease, border-radius 0.3s ease",
  textTransform: "none",
  width: "fit-content"
};

/** Hover shape must win over global themeOverrides (e.g. MuiButton text borderRadius 100px) and Survey CSS. */
const glButtonHoverRadius = "16px 8px 16px 16px !important";

const variantStylesMap: Record<string, StylesObject> = {
  primary: {
    backgroundColor: COLORS.PRIMARY,
    margin: 0,
    typography: { xs: "body_strong_white", md: "body_strong_lg_white" },
    "&:hover:not(.Mui-disabled)": {
      backgroundColor: COLORS.PRIMARY_DARK,
      borderRadius: glButtonHoverRadius
    },
    "&.Mui-disabled": {
      backgroundColor: COLORS.GRAY,
      color: COLORS.DARK_GRAY
    }
  },
  secondary: {
    backgroundColor: "white",
    border: `1px solid ${COLORS.PRIMARY}`,
    margin: 0,
    typography: { xs: "body_strong_primary", md: "body_strong_lg_primary" },
    "&:hover:not(.Mui-disabled)": {
      backgroundColor: COLORS.PRIMARY_LIGHTEST,
      borderRadius: glButtonHoverRadius
    }
  },
  "secondary-transparent": {
    backgroundColor: "transparent",
    border: `1px solid ${COLORS.PRIMARY}`,
    margin: 0,
    typography: { xs: "body_strong_primary", md: "body_strong_lg_primary" },
    "&:hover:not(.Mui-disabled)": {
      backgroundColor: COLORS.PRIMARY_LIGHTEST,
      borderRadius: glButtonHoverRadius
    }
  },
  "secondary-no-border": {
    backgroundColor: "transparent",
    border: "none",
    margin: 0,
    typography: { xs: "body_strong_primary", md: "body_strong_lg_primary" },
    "&:hover:not(.Mui-disabled)": {
      backgroundColor: COLORS.PRIMARY_LIGHTEST,
      borderRadius: glButtonHoverRadius
    }
  }
};

const GLButton: React.FC<React.PropsWithChildren<GLButtonProps>> = ({
  buttonType = "primary",
  children,
  loading,
  onClick,
  disabled,
  text,
  leftIcon,
  rightIcon,
  sx,
  id,
  title = ""
}) => {
  const buttonStyles: Record<string, any> = {
    ...baseStyles,
    ...variantStylesMap[buttonType]
  };

  const mergedSx = deepmerge(buttonStyles, sx ?? {}) as SxProps;

  const buttonText = children ?? (
    <Typography
      sx={{
        typography: buttonStyles?.typography
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Button
      className="gl-button"
      component="button"
      disabled={loading || disabled}
      onClick={onClick}
      sx={mergedSx}
      id={id}
      title={title || ""}
    >
      {leftIcon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginRight: "8px"
          }}
        >
          {leftIcon}
        </Box>
      )}
      {buttonText}
      {loading ? (
        <Spinner />
      ) : (
        rightIcon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginLeft: "8px",
              typography: buttonStyles?.typography
            }}
          >
            {rightIcon}
          </Box>
        )
      )}
    </Button>
  );
};

export default GLButton;
